import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Zap,
  TrendingUp,
} from 'lucide-react';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../../theme/netflixTheme';
import { useSubscription } from '../../hooks/useSubscription';
import toast from 'react-hot-toast';

export function BillingDashboard({ onBack }: { onBack: () => void }) {
  const navigate = useNavigate();
  const {
    subscription,
    paymentHistory,
    stats,
    isLoading,
    isSubscriptionActive,
    daysUntilExpiration,
  } = useSubscription();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button style={backButtonStyle} onClick={onBack}>
          <ArrowLeft size={24} color={NETFLIX_COLORS.textPrimary} />
        </button>
        <h1 style={titleStyle}>Faturamento</h1>
      </div>

      {isLoading ? (
        <div style={loadingStyle}>
          <p>Carregando...</p>
        </div>
      ) : (
        <div style={contentStyle}>
          {/* Status Card */}
          {subscription && (
            <div
              style={{
                ...statusCardStyle,
                ...(isSubscriptionActive
                  ? {
                      borderColor: NETFLIX_COLORS.primary,
                      backgroundColor: `${NETFLIX_COLORS.primary}12`,
                      borderWidth: '2px',
                    }
                  : {
                      borderColor: '#EF4444',
                      backgroundColor: '#EF444412',
                      borderWidth: '2px',
                    }),
              }}
            >
              <div style={statusHeaderStyle}>
                {isSubscriptionActive ? (
                  <CheckCircle size={32} color={NETFLIX_COLORS.primary} />
                ) : (
                  <AlertCircle size={32} color="#EF4444" />
                )}
                <div>
                  <p style={statusTitleStyle}>
                    {isSubscriptionActive ? 'Assinatura Ativa' : 'Assinatura Inativa'}
                  </p>
                  <p style={statusSubtitleStyle}>
                    {isSubscriptionActive
                      ? `Válida por mais ${daysUntilExpiration} dias`
                      : 'Renove agora para continuar assistindo'}
                  </p>
                </div>
              </div>

              <div style={statusDetailsStyle}>
                <div style={statusDetailStyle}>
                  <p style={statusLabelStyle}>Plano</p>
                  <p style={statusValueStyle}>{subscription.plan.toUpperCase()}</p>
                </div>
                <div style={statusDetailStyle}>
                  <p style={statusLabelStyle}>Valor Mensal</p>
                  <p style={statusValueStyle}>{formatCurrency(subscription.amount)}</p>
                </div>
                <div style={statusDetailStyle}>
                  <p style={statusLabelStyle}>Expira em</p>
                  <p style={statusValueStyle}>{formatDate(subscription.expires_at)}</p>
                </div>
              </div>

              {isSubscriptionActive && (
                <button style={renewButtonStyle}>
                  <Zap size={16} />
                  Renovar Agora
                </button>
              )}
            </div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div style={statsGridStyle}>
              <div style={statCardStyle}>
                <div style={statIconStyle}>
                  <DollarSign size={24} color={NETFLIX_COLORS.primary} />
                </div>
                <div>
                  <p style={statLabelStyle}>Total Gasto</p>
                  <p style={statValueStyle}>{formatCurrency(stats.totalSpent)}</p>
                </div>
              </div>

              <div style={statCardStyle}>
                <div style={statIconStyle}>
                  <Calendar size={24} color={NETFLIX_COLORS.primary} />
                </div>
                <div>
                  <p style={statLabelStyle}>Pagamentos</p>
                  <p style={statValueStyle}>{stats.paymentsCount}</p>
                </div>
              </div>

              <div style={statCardStyle}>
                <div style={statIconStyle}>
                  <TrendingUp size={24} color={NETFLIX_COLORS.primary} />
                </div>
                <div>
                  <p style={statLabelStyle}>Valor Médio</p>
                  <p style={statValueStyle}>
                    {stats.paymentsCount > 0
                      ? formatCurrency(stats.totalSpent / stats.paymentsCount)
                      : 'R$ 0,00'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Recent Payments */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Histórico de Pagamentos</h2>
                <p style={sectionSubtitleStyle}>
                  Últimos 3 pagamentos
                </p>
              </div>
              <button
                style={viewAllButtonStyle}
                onClick={() => navigate('/profile/billing/history')}
              >
                Ver Todos →
              </button>
            </div>

            {paymentHistory.length === 0 ? (
              <div style={emptyStateStyle}>
                <AlertCircle size={32} color={NETFLIX_COLORS.textSecondary} />
                <p style={emptyTextStyle}>Nenhum pagamento registrado</p>
              </div>
            ) : (
              <div style={paymentsListStyle}>
                {paymentHistory.slice(0, 3).map((payment) => (
                  <div
                    key={payment.id}
                    style={{
                      ...paymentItemStyle,
                      ...(payment.payment_status === 'paid' ? {
                        backgroundColor: `${NETFLIX_COLORS.primary}15`,
                        borderLeft: `5px solid ${NETFLIX_COLORS.primary}`,
                      } : {})
                    }}
                  >
                    <div style={paymentInfoStyle}>
                      <div>
                        <p style={paymentPlanStyle}>{payment.plan.toUpperCase()}</p>
                        <p style={paymentDateStyle}>
                          {formatDate(payment.started_at)}
                          {payment.expires_at && ` - até ${formatDate(payment.expires_at)}`}
                        </p>
                      </div>
                    </div>
                    <div style={paymentStatusContainerStyle}>
                      <div style={paymentAmountStyle}>
                        <strong>{formatCurrency(payment.amount)}</strong>
                      </div>
                      <div
                        style={{
                          ...paymentStatusBadgeStyle,
                          ...(payment.payment_status === 'paid' ? {
                            backgroundColor: NETFLIX_COLORS.primary,
                            color: NETFLIX_COLORS.white,
                          } : payment.payment_status === 'pending' ? {
                            backgroundColor: '#F59E0B',
                            color: NETFLIX_COLORS.white,
                          } : {
                            backgroundColor: '#EF4444',
                            color: NETFLIX_COLORS.white,
                          })
                        }}
                      >
                        {payment.payment_status === 'paid'
                          ? 'PAGO'
                          : payment.payment_status === 'pending'
                          ? 'PENDENTE'
                          : 'FALHOU'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Support Section */}
          <div style={supportSectionStyle}>
            <h3 style={supportTitleStyle}>Precisa de Ajuda?</h3>
            <p style={supportTextStyle}>
              Se tiver dúvidas sobre seu faturamento ou assinatura, entre em contato com nosso suporte.
            </p>
            <button
              style={supportButtonStyle}
              onClick={() => toast.success('Suporte será redirecionado em breve')}
            >
              Contatar Suporte
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const containerStyle: React.CSSProperties = {
  flex: 1,
  backgroundColor: NETFLIX_COLORS.background,
  color: NETFLIX_COLORS.textPrimary,
  overflowY: 'auto',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: `${NETFLIX_SPACING.xl}px`,
  backgroundColor: NETFLIX_COLORS.surface,
  borderBottom: `1px solid ${NETFLIX_COLORS.border}`,
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

const backButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 0.3s ease',
};

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: '700',
  margin: 0,
};

const contentStyle: React.CSSProperties = {
  padding: `${NETFLIX_SPACING.xl}px`,
  maxWidth: '1200px',
  margin: '0 auto',
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  fontSize: 16,
  color: NETFLIX_COLORS.textSecondary,
};

const statusCardStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  border: `2px solid`,
  borderRadius: '16px',
  padding: `${NETFLIX_SPACING.xl}px`,
  marginBottom: `${NETFLIX_SPACING.xl}px`,
};

const statusHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '24px',
  paddingBottom: '24px',
  borderBottom: `1px solid ${NETFLIX_COLORS.border}`,
};

const statusTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: '700',
  margin: 0,
  color: NETFLIX_COLORS.textPrimary,
};

const statusSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: NETFLIX_COLORS.textSecondary,
  margin: '6px 0 0 0',
};

const statusDetailsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: '24px',
  marginBottom: '24px',
};

const statusDetailStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const statusLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: '600',
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const statusValueStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: '700',
  color: NETFLIX_COLORS.textPrimary,
  margin: 0,
};

const renewButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '100%',
  backgroundColor: NETFLIX_COLORS.primary,
  color: NETFLIX_COLORS.white,
  border: 'none',
  borderRadius: '10px',
  padding: '14px 16px',
  fontSize: 14,
  fontWeight: '700',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: `0 4px 12px ${NETFLIX_COLORS.primary}40`,
};

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: `${NETFLIX_SPACING.xl}px`,
};

const statCardStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  border: `2px solid ${NETFLIX_COLORS.border}`,
  borderRadius: '14px',
  padding: `${NETFLIX_SPACING.lg * 1.2}px`,
  display: 'flex',
  alignItems: 'center',
  gap: '18px',
  transition: 'all 0.3s ease',
};

const statIconStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '50px',
  height: '50px',
  backgroundColor: `${NETFLIX_COLORS.primary}15`,
  borderRadius: '12px',
  flexShrink: 0,
};

const statLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: '700',
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const statValueStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: '700',
  color: NETFLIX_COLORS.primary,
  margin: '8px 0 0 0',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: `${NETFLIX_SPACING.xl * 1.5}px`,
  paddingBottom: `${NETFLIX_SPACING.lg}px`,
  borderBottom: `1px solid ${NETFLIX_COLORS.border}`,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '20px',
  gap: '20px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: '700',
  margin: 0,
  color: NETFLIX_COLORS.textPrimary,
};

const sectionSubtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: NETFLIX_COLORS.textSecondary,
  margin: '4px 0 0 0',
};

const viewAllButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: NETFLIX_COLORS.primary,
  fontSize: 13,
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  padding: '8px 12px',
  borderRadius: '6px',
};

const emptyStateStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  textAlign: 'center',
};

const emptyTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: NETFLIX_COLORS.textSecondary,
  margin: '12px 0 0 0',
};

const paymentsListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const paymentItemStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  border: `1px solid ${NETFLIX_COLORS.border}`,
  borderRadius: '12px',
  padding: `${NETFLIX_SPACING.lg}px ${NETFLIX_SPACING.xl}px`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  transition: 'all 0.3s ease',
};

const paymentInfoStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flex: 1,
  gap: '24px',
};

const paymentPlanStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: '700',
  margin: 0,
  color: NETFLIX_COLORS.textPrimary,
  letterSpacing: '0.5px',
};

const paymentDateStyle: React.CSSProperties = {
  fontSize: 12,
  color: NETFLIX_COLORS.textSecondary,
  margin: '6px 0 0 0',
  lineHeight: '1.4',
};

const paymentAmountStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: '700',
  color: NETFLIX_COLORS.primary,
  minWidth: '100px',
  textAlign: 'right',
};

const paymentStatusContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
};

const paymentStatusBadgeStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: '8px',
  fontWeight: '600',
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

const supportSectionStyle: React.CSSProperties = {
  backgroundColor: `${NETFLIX_COLORS.primary}15`,
  border: `1px solid ${NETFLIX_COLORS.primary}40`,
  borderRadius: '12px',
  padding: `${NETFLIX_SPACING.lg}px`,
  textAlign: 'center',
};

const supportTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: '700',
  margin: 0,
  color: NETFLIX_COLORS.textPrimary,
};

const supportTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: NETFLIX_COLORS.textSecondary,
  margin: '8px 0',
  lineHeight: '1.5',
};

const supportButtonStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.primary,
  color: NETFLIX_COLORS.white,
  border: 'none',
  borderRadius: '8px',
  padding: '10px 20px',
  fontSize: 13,
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
};
